export interface ResponseWithVersion<T> {
  version: string;
  data: T;
}

export const apiFetch = (
  url: string,
  workgroupId?: number,
  method: 'POST' | 'PUT' | 'GET' = 'GET'
): Promise<Response> => {
  const headers: { [key: string]: string } = {
    'Content-Type': 'application/json',
  };
  if (!headers[HARMONY_IMPERSONATE_WORKGROUP_HEADER_NAME] && workgroupId) {
    headers[HARMONY_IMPERSONATE_WORKGROUP_HEADER_NAME] = workgroupId.toString();
  }

  var token = process.env.REACT_APP_TOKEN;
  if(token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return fetch(url, { method, headers }).then((response) => {
    if (!response.ok) {
      throw Error(response.statusText);
    }
    return response;
  });
};

export const fetchHelper = (
  url: string,
  verb: string,
  data = {},
  workgroupId?: number,
  headers?: { [key: string]: string }
): Promise<Response> => {
  const requestHeaders: HeadersInit = {
    'Content-Type': 'application/json',
    ...headers,
  };
  if (!requestHeaders[HARMONY_IMPERSONATE_WORKGROUP_HEADER_NAME] && workgroupId) {
    requestHeaders[HARMONY_IMPERSONATE_WORKGROUP_HEADER_NAME] = workgroupId.toString();
  }

  
  var token = process.env.REACT_APP_TOKEN;
  if(token) {
    requestHeaders['Authorization'] = `Bearer ${token}`;
  }

  return fetch(url, {
    method: verb, // *GET, POST, PUT, DELETE, etc.
    // mode: "cors", // no-cors, cors, *same-origin
    credentials: 'include',
    cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
    headers: requestHeaders,
    body: JSON.stringify(data),
  });
};

export const getWithVersion = async <T>(
  url: string,
  workgroupId?: number
): Promise<ResponseWithVersion<T>> => {
  const response = await apiFetch(url, workgroupId);
  const data = await response.json();
  const version = response.headers.get('ETag') || '';

  return {
    data,
    version,
  };
};

export const putWithVersion = async <T>(
  url: string,
  data: any,
  version: string,
  workgroupId?: number,
  headers?: { [key: string]: string }
): Promise<{ data: T; version: string }> => {
  let computedHeaders = { ...headers, ETag: version };
  const etagRegex = /^(W\/)?"(.*)"$/;

  // HS-3048
  // when adding the ETag header as the model version, test if the version is enclosed in double quotes and enclose it if not
  if (typeof version == 'string') {
    computedHeaders = { ...headers, ETag: etagRegex.test(version) ? version : `"${version}"` };
  }

  const response = await fetchHelper(url, 'PUT', data, workgroupId, computedHeaders);
  const responseData = await response.json();

  // the model property RowVersion should have the enclosing double quotes stripped
  const responseEtag = response.headers.get('ETag') || '""';
  const matches = responseEtag.match(etagRegex);
  version = matches && matches[2] ? matches[2] : '';

  return {
    data: responseData,
    version: version,
  };
};

export const postWithReturnedVersion = async <T>(
  url: string,
  data: any,
  workgroupId?: number,
  headers?: { [key: string]: string }
): Promise<{ data: T; version: string }> => {
  const etagRegex = /^(W\/)?"(.*)"$/;
  const response = await fetchHelper(url, 'POST', data, workgroupId, headers);
  const responseData = await response.json();

  // the model property RowVersion should have the enclosing double quotes stripped
  const responseEtag = response.headers.get('ETag') || '""';
  const matches = responseEtag.match(etagRegex);
  const version = matches && matches[2] ? matches[2] : '';

  return {
    data: responseData,
    version: version,
  };
};

export const HARMONY_IMPERSONATE_WORKGROUP_HEADER_NAME: string =
  'x-harmony-impersonated-workgroupId';
